/**
 * Serviço de token exchange cross-tenant (cliente roteado).
 *
 * Chama POST /auth/cross-tenant-exchange usando o token GLOBAL da sessão
 * (o backend valida a origem pelo Bearer atual) e retorna o token de operação
 * para o tenant destino. O resultado alimenta o QuotationOperationContextStore.
 */

import { api } from "../../../../lib/api";
import { sendToTerminal } from "../../../../lib/terminalLogger";
import { LogCategory } from "../../../../config/logging";
import type { QuotationOperationContext } from "./quotationOperationContext";

export interface CrossTenantExchangeParams {
	targetTenantId: string;
	targetClientId: number;
	routingConfigId: number;
	sourceTenantId?: string | null;
}

interface CrossTenantExchangeResponse {
	success: boolean;
	data: {
		operationToken: string;
		effectiveTenantId: string;
		expiresAt: string;
		expiresInSeconds: number;
		targetUser: {
			user_id: number;
			user_login: string;
			user_nome: string | null;
		};
	};
}

/**
 * Solicita o token de operação para o tenant destino e monta o contexto.
 * Não persiste nada global; o chamador injeta o resultado no store de operação.
 */
export async function requestCrossTenantOperation(
	params: CrossTenantExchangeParams,
): Promise<QuotationOperationContext> {
	sendToTerminal(
		"🔁 [CROSS_TENANT] Solicitando token de operação",
		{
			targetTenantId: params.targetTenantId,
			targetClientId: params.targetClientId,
		},
		LogCategory.API,
	);

	const response = (await api("/auth/cross-tenant-exchange", {
		method: "POST",
		body: JSON.stringify({
			targetTenantId: params.targetTenantId,
			targetClientId: params.targetClientId,
		}),
	})) as CrossTenantExchangeResponse;

	if (!response?.success || !response.data?.operationToken) {
		throw new Error("Falha ao obter token de operação cross-tenant.");
	}

	const { data } = response;

	return {
		sourceTenantId: params.sourceTenantId ?? null,
		effectiveTenantId: data.effectiveTenantId,
		operationAccessToken: data.operationToken,
		routing: {
			routingConfigId: params.routingConfigId,
			targetClientId: params.targetClientId,
			targetUserId: data.targetUser.user_id,
			targetUserLogin: data.targetUser.user_login,
		},
		expiresAt: data.expiresAt,
	};
}
